/**
 * Modal de soumission d'un reçu de paiement (ou justificatif gratuité/bourse).
 * Style sobre, cohérent avec le reste du portail (gray-900, blue-600, pas de gradients).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Upload, X, FileImage, Camera, CheckCircle } from 'lucide-react';
import { portailApi, StatutPaiementMois } from '../services/portailApi';
import { compressImage, formatBytes } from '../../utils/imageCompression';
import { CameraCaptureModal } from '../../components/CameraCaptureModal';

// Année académique : novembre → août + inscription + examen (2 semestres).
const PERIODES = [
  { code: 'INSC', label: 'Inscription / Réinscription' },
  { code: 'NOV', label: 'Novembre' },
  { code: 'DEC', label: 'Décembre' },
  { code: 'JAN', label: 'Janvier' },
  { code: 'FEV', label: 'Février' },
  { code: 'MAR', label: 'Mars' },
  { code: 'AVR', label: 'Avril' },
  { code: 'MAI', label: 'Mai' },
  { code: 'JUIN', label: 'Juin' },
  { code: 'JUIL', label: 'Juillet' },
  { code: 'AOUT', label: 'Août' },
  { code: 'EXAM', label: 'Examen (2 semestres — 30 000 FCFA)' },
] as const;

const TYPES = [
  { code: 'paiement', label: 'Reçu de paiement' },
  { code: 'gratuite', label: 'Gratuité' },
  { code: 'bourse', label: 'Bourse' },
  { code: 'autre', label: 'Autre' },
] as const;

interface Props {
  onClose: () => void;
  onSubmitted: () => void;
}

export const SoumettreRecuModal: React.FC<Props> = ({ onClose, onSubmitted }) => {
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [typeDemande, setTypeDemande] = useState<typeof TYPES[number]['code']>('paiement');
  const [periodes, setPeriodes] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [tailleInfo, setTailleInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const galerieInputRef = useRef<HTMLInputElement>(null);
  // Fix 2026-08-31 : charger le statut de paiement par période pour ne
  // proposer QUE les rubriques avec dette (celles à jour sont grisées).
  const [statutPaiements, setStatutPaiements] = useState<StatutPaiementMois[]>([]);
  useEffect(() => {
    portailApi.me()
      .then(r => { setStatutPaiements(r.statut_paiements || []); })
      .catch(() => { /* silencieux — on garde le comportement d'avant */ });
  }, []);
  const statutParPeriode = React.useMemo(() => {
    const m = new Map<string, StatutPaiementMois>();
    statutPaiements.forEach(s => m.set(s.periode, s));
    return m;
  }, [statutPaiements]);

  const togglePeriode = (code: string) => {
    // Interdire la sélection d'une période déjà à jour (pas de dette).
    const st = statutParPeriode.get(code);
    if (st?.est_a_jour) return;
    setPeriodes((prev) => prev.includes(code) ? prev.filter((p) => p !== code) : [...prev, code]);
  };

  const acceptFile = async (f: File) => {
    if (f.size > 15 * 1024 * 1024) {
      setError('Photo trop volumineuse (max 15 Mo avant compression)');
      return;
    }
    setError(null);
    setCompressing(true);
    setTailleInfo(null);
    try {
      const { file: outFile, originalSize, compressedSize } = await compressImage(f);
      setPhoto(outFile);
      if (outFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
        reader.readAsDataURL(outFile);
        setTailleInfo(compressedSize < originalSize
          ? `Compressée : ${formatBytes(originalSize)} → ${formatBytes(compressedSize)}`
          : formatBytes(compressedSize));
      } else {
        setPhotoPreview(null);
        setTailleInfo(formatBytes(compressedSize));
      }
    } finally {
      setCompressing(false);
    }
  };

  const onPhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await acceptFile(f);
    if (galerieInputRef.current) galerieInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!photo) {
      setError('Veuillez ajouter une photo du reçu (ou justificatif).');
      return;
    }
    if (typeDemande === 'paiement' && periodes.length === 0) {
      setError('Sélectionnez au moins une période concernée par ce paiement.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('photo', photo);
      fd.append('type_demande', typeDemande);
      fd.append('periodes_concernees', JSON.stringify(periodes));
      if (message.trim()) fd.append('message_etudiant', message.trim());

      await portailApi.soumettreDemandeActivation(fd);
      onSubmitted();
    } catch (err: any) {
      setError(err?.message || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3">
      <div className="bg-gray-900 border border-gray-800 rounded-t-xl sm:rounded-xl shadow-2xl w-full max-w-lg max-h-[95vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
          <h3 className="font-semibold text-gray-100">Soumettre un reçu</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto p-5 space-y-4">
          {/* Type de demande */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Type de demande</label>
            <div className="grid grid-cols-2 gap-2">
              {TYPES.map((t) => (
                <button
                  key={t.code}
                  type="button"
                  onClick={() => setTypeDemande(t.code)}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                    typeDemande === t.code
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Photo — caméra directe OU galerie/fichier */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">
              Photo du reçu / justificatif <span className="text-red-400">*</span>
            </label>

            {/* Input galerie caché — la caméra passe par CameraCaptureModal */}
            <input
              ref={galerieInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,application/pdf"
              onChange={onPhotoChange}
              className="hidden"
            />

            {photoPreview ? (
              <div className="border border-gray-700 rounded-lg p-3 space-y-2">
                <img src={photoPreview} alt="Aperçu" className="max-h-48 mx-auto rounded" />
                <div className="text-center">
                  <p className="text-xs text-gray-300 truncate">{photo?.name}</p>
                  {tailleInfo && <p className="text-[10px] text-emerald-400 mt-0.5">{tailleInfo}</p>}
                </div>
              </div>
            ) : photo ? (
              <div className="border border-gray-700 rounded-lg p-3 flex items-center gap-2 justify-center text-gray-300">
                <FileImage size={18} />
                <span className="text-sm truncate">{photo.name}</span>
                {tailleInfo && <span className="text-[10px] text-emerald-400">· {tailleInfo}</span>}
              </div>
            ) : compressing ? (
              <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 flex items-center justify-center gap-2 text-gray-400">
                <Loader2 size={16} className="animate-spin" />
                <span className="text-sm">Compression en cours…</span>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setShowCamera(true)}
                  className="flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-gray-700 hover:border-blue-500 rounded-lg p-4 text-gray-300 transition-colors"
                >
                  <Camera size={24} />
                  <span className="text-sm font-medium">Prendre une photo</span>
                  <span className="text-[10px] text-gray-500">Caméra directe</span>
                </button>
                <button
                  type="button"
                  onClick={() => galerieInputRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-gray-700 hover:border-blue-500 rounded-lg p-4 text-gray-300 transition-colors"
                >
                  <Upload size={24} />
                  <span className="text-sm font-medium">Depuis la galerie</span>
                  <span className="text-[10px] text-gray-500">JPG, PNG, PDF</span>
                </button>
              </div>
            )}

            {photo && !compressing && (
              <button
                type="button"
                onClick={() => { setPhoto(null); setPhotoPreview(null); setTailleInfo(null); }}
                className="mt-2 text-[11px] text-gray-400 hover:text-gray-200 underline"
              >
                Changer la photo
              </button>
            )}
          </div>

          {/* Périodes concernées (uniquement pour paiement) */}
          {typeDemande === 'paiement' && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                Périodes couvertes par ce reçu <span className="text-red-400">*</span>
              </label>
              <div className="grid grid-cols-3 gap-1.5">
                {PERIODES.map((p) => {
                  const sel = periodes.includes(p.code);
                  const st = statutParPeriode.get(p.code);
                  const aJour = !!st?.est_a_jour;
                  return (
                    <button
                      key={p.code}
                      type="button"
                      onClick={() => togglePeriode(p.code)}
                      disabled={aJour}
                      title={aJour ? 'Cette rubrique est déjà à jour — aucun impayé à justifier.' : ''}
                      className={`px-2 py-1.5 text-xs rounded border transition-colors flex items-center justify-center gap-1 ${
                        aJour
                          ? 'bg-green-900/20 border-green-700 text-green-400 line-through opacity-70 cursor-not-allowed'
                          : sel
                            ? 'bg-blue-600 border-blue-600 text-white font-medium'
                            : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                      }`}
                    >
                      {aJour && <CheckCircle size={10} />}
                      {p.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                Cochez les mois couverts par ce reçu.
                {statutPaiements.length > 0 && (
                  <span className="text-green-400"> Les rubriques déjà à jour (✓) sont désactivées.</span>
                )}
              </p>
            </div>
          )}

          {/* Message */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Message (facultatif)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Précisions à l'attention de la caisse..."
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-gray-100 text-sm placeholder-gray-500"
            />
          </div>

          {error && (
            <div className="p-2.5 bg-red-900/30 border border-red-800 rounded text-xs text-red-300">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-300 hover:text-gray-100 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {submitting ? 'Envoi...' : 'Soumettre la demande'}
            </button>
          </div>
        </form>
      </div>

      {showCamera && (
        <CameraCaptureModal
          filenamePrefix="recu"
          onClose={() => setShowCamera(false)}
          onCapture={async (file) => {
            setShowCamera(false);
            await acceptFile(file);
          }}
        />
      )}
    </div>
  );
};
